<template>
  <div
    class="nav-list-item"
    :class="{
      'is-selected': selected,
      'is-retracted': isRetracted,
      'has-icon': withIcon,
      'is-full-width': fullWidth,
    }"
    :style="itemStyle"
    role="button"
    tabindex="0"
    :aria-pressed="selected"
    :aria-label="label"
    @click="onClick"
    @keydown.enter.prevent="onClick"
    @keydown.space.prevent="onClick"
  >
    <span v-if="withIcon" class="nav-list-item__icon" aria-hidden="true">
      <slot name="icon" />
    </span>
    <span v-if="!isRetracted" class="nav-list-item__label">
      <slot>{{ label }}</slot>
    </span>
  </div>
</template>

<script setup>
const props = defineProps({
  label: { type: String, default: "Label" },
  selected: { type: Boolean, default: false },
  withIcon: { type: Boolean, default: false },
  retracted: { type: Boolean, default: false },
  fullWidth: { type: Boolean, default: false },
  /** Largeur du item (ignorée si retracted ou fullWidth). Ex: 256, '256px', '100%' */
  width: { type: [String, Number], default: 256 },
});

const emit = defineEmits(["click"]);

const isRetracted = computed(() => props.retracted && props.withIcon);

const itemStyle = computed(() => {
  if (isRetracted.value) {
    return { width: "auto" };
  }
  if (props.fullWidth) {
    return { width: "100%" };
  }
  const w = props.width;
  return {
    width: typeof w === "number" ? `${w}px` : w,
  };
});

function onClick(event) {
  emit("click", event);
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.nav-list-item {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: $neutral_light_surface_white;
  color: $neutral_dark_normal;
  cursor: pointer;
  text-align: left;
  user-select: none;
  transition: background-color 0.15s ease, color 0.15s ease;
  @include label-medium;

  &.is-full-width {
    display: flex;
  }

  &:hover {
    background-color: $vert_surface_white_hover;
  }

  &:active {
    background-color: $vert_surface_white_active;
  }

  &.is-selected {
    background-color: $vert_normal;
    color: $neutral_light_light;
    @include label-medium-emphasized;

    &:hover {
      background-color: $vert_normal_hover;
    }

    &:active {
      background-color: $vert_normal_active;
    }
  }

  &.is-retracted {
    justify-content: center;
    gap: 0;
  }

  &__icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: inherit;

    :deep(svg),
    :deep(img),
    :deep(i) {
      width: 18px;
      height: 18px;
      font-size: 18px;
      color: inherit;
      fill: currentColor;
    }
  }

  &__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
